import React from "react";
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Container,
  Grid,
  Box,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import styles from "./FAQ.module.css";
import { Helmet } from "react-helmet";

const FAQ = () => {
  const faqData = [
    {
      question: "How does AI Background Changer work?",
      answer:
        "Our AI Background Changer uses advanced deep learning algorithms to automatically detect and separate subjects from their backgrounds with over 95% accuracy. It preserves fine details like hair, fur, and complex edges without requiring manual editing.",
    },
    {
      question: "Can I use this for e-commerce product photos?",
      answer:
        "Absolutely! Our AI Background Changer is ideal for e-commerce. You can instantly remove backgrounds and replace them with pure white, gradients, or custom environments. Batch processing is also supported for handling large product catalogs.",
    },
    {
      question: "What file formats are supported?",
      answer:
        "We support all major formats, including PNG (with transparent backgrounds), JPG, and WEBP, with resolutions up to 4K. You can export edited images with or without an alpha channel based on your needs.",
    },
    {
      question: "Is there a limit on image resolution?",
      answer:
        "The free tier supports up to 1080p HD resolution. Pro users can access 4K background removal and replacement (3840×2160 pixels) with priority processing for faster results.",
    },
    {
      question: "How accurate is the AI with complex subjects?",
      answer:
        "Our AI excels at handling challenging cases, such as fine hair or fur detection, translucent objects, complex edges, and shadow preservation. For perfect results, use the refine tool to fine-tune edges.",
    },
    {
      question: "Can I edit the AI-generated backgrounds?",
      answer:
        "Yes! After automatic background removal, you can use our integrated editor to adjust color matching, add shadows or reflections, blend multiple backgrounds, and apply professional presets for a polished look.",
    },
    {
      question: "Do you store my uploaded images?",
      answer:
        "All images are processed securely and automatically deleted after 24 hours. We never use your content for AI training or share it with third parties.",
    },
    {
      question: "How does this compare to Photoshop?",
      answer:
        "Our AI Background Changer offers several advantages: 10x faster processing, one-click complex selections, no design skills required, web-based access, and cost-effective pricing.",
    },
    {
      question: "Can I use this on mobile devices?",
      answer:
        "Yes! Our web-based AI Background Changer is fully optimized for mobile devices. It features touch-friendly controls and automatically resizes images for seamless use on smaller screens.",
    },
    {
      question: "Is commercial use allowed?",
      answer:
        "Yes, all removed or replaced backgrounds come with full commercial rights. No attribution is required, so you can use them for client work, social media, print materials, and merchandise.",
    },
  ];

  // Create schema markup for FAQ
  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqData.map((faq) => ({
      "@type": "Question",
      name: faq.question,
      acceptedAnswer: {
        "@type": "Answer",
        text: faq.answer,
      },
    })),
  };

  return (
    <Container maxWidth="lg" className={styles.container}>
      <Helmet>
        {/* Structured Data */}
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      </Helmet>
      <Typography variant="h2" className={styles.heading}>
        AI Background Changer FAQs
      </Typography>
      <Typography variant="h6" className={styles.subheading}>
        Professional Solutions for Background Removal & Editing
      </Typography>

      <Grid container justifyContent="center">
        <Grid item xs={12} md={10}>
          {faqData.map((faq, index) => (
            <Accordion
              key={index}
              className={styles.accordion}
              sx={{
                backgroundColor: "#f9f9f9",
                margin: "8px 0",
                boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon className={styles.expandIcon} />}
                aria-controls={`panel${index}-content`}
                id={`panel${index}-header`}
              >
                <Typography
                  variant="h6"
                  className={styles.question}
                  sx={{ fontWeight: 600 }}
                >
                  {faq.question}
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Typography
                  className={styles.answer}
                  sx={{
                    color: "#444",
                    lineHeight: 1.6,
                    padding: "0 0 16px 0",
                  }}
                >
                  {faq.answer}
                </Typography>
              </AccordionDetails>
            </Accordion>
          ))}
        </Grid>
      </Grid>
    </Container>
  );
};

export default FAQ;
